import { useMemo, useRef } from 'react'
import { t } from 'i18next'

import { Spinner } from '@/components/ui/spinner'
import Folder from '@/public/images/folder.svg'
import { SortOrder } from '@/types/common'
import { cn } from '@/utils'

import TableBody from './TableBody'
import { TableColumn, TableHead } from './TableHead'
import TablePagination from './TablePagination'

import styles from './Table.module.scss'

export type TableProps<T extends object = Record<string, unknown>> = {
  columns: TableColumn<T>[]
  data: T[]
  className?: string
  onRowClick?: (row: T) => void
  isLoading?: boolean
  noDataMessage?: string | React.ReactNode

  orderBy?: keyof T | null
  order?: SortOrder
  onSortChange?: (colId: keyof T | null, dir: SortOrder) => void

  page?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  roundedTop?: boolean
  roundedBottom?: boolean
  fullHeight?: boolean
}

export function TableComponent<T extends object = Record<string, unknown>>({
  columns,
  data,
  className,
  onRowClick,
  isLoading = false,
  noDataMessage,
  orderBy = null,
  order = null,
  onSortChange,
  page = 0,
  totalPages = 1,
  onPageChange,
  roundedTop = false,
  roundedBottom = false,
  fullHeight = false,
}: Readonly<TableProps<T>>) {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={columns.length} className="p-0">
              <div className="flex items-center justify-center bg-white h-[480px]">
                <Spinner className="size-8 text-brand-600" />
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    if (data.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={columns.length} className="p-0">
              <div className="flex flex-col items-center justify-center gap-3 text-center bg-white h-[480px]">
                {noDataMessage || (
                  <div>
                    <img
                      src={Folder}
                      alt="No data"
                      className="h-12 mb-4 w-14"
                    />
                    <div className="flex flex-col gap-1">
                      <h3 className="p-0 m-0 font-semibold text-primary-900 text-md">
                        {t('components.table.noData.title')}
                      </h3>
                      <p className="p-0 m-0 text-sm text-tertiary-600">
                        {t('components.table.noData.description')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    return <TableBody columns={columns} data={data} onRowClick={onRowClick} />
  }

  const fullHeightClass = useMemo(() => {
    if (!fullHeight) return ''

    if (totalPages > 1) return 'max-h-[calc(100%-69px)]'
    return 'rounded-b-lg'
  }, [fullHeight, totalPages])

  const containerFullHeightClass = useMemo(() => {
    if (!fullHeight) return ''

    return 'max-h-none h-full'
  }, [fullHeight])

  return (
    <div className={className}>
      <div
        className={cn(
          'relative border border-gray-light-300 border-solid',
          roundedTop ? 'rounded-t-lg' : '',
          roundedBottom ? 'rounded-b-lg' : '',
          'overflow-hidden',
          fullHeightClass
        )}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            'overflow-auto',
            styles.tableContainer,
            isLoading || data.length === 0
              ? 'max-h-[480px]'
              : 'max-h-[calc(100vh-320px)]',
            containerFullHeightClass
          )}
        >
          <table className="min-w-full text-sm table-fixed border-spacing-0">
            <TableHead
              columns={columns}
              orderBy={orderBy}
              order={order}
              onSort={onSortChange ?? (() => {})}
              isLoading={isLoading}
            />
            {renderTableContent()}
          </table>
        </div>
      </div>

      {onPageChange && totalPages > 1 && (
        <div className="flex justify-end bg-white border border-t-0 border-b border-[--Colors-Gray-light-300] border-solid rounded-b-2xl">
          <TablePagination
            page={page}
            totalPages={totalPages}
            setPage={onPageChange}
          />
        </div>
      )}
    </div>
  )
}

export const Table = TableComponent as <
  T extends object = Record<string, unknown>,
>(
  props: TableProps<T>
) => JSX.Element
